import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { mcpModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ProviderMark from './provider-mark';

const oauthKeys = ['client_id', 'redirect_uri', 'response_type', 'scope', 'state', 'code_challenge', 'code_challenge_method', 'resource'];
const requiredOAuthKeys = oauthKeys.filter((key) => key !== 'state');

const providerFromRedirect = (redirectURI) => {
  try {
    const host = new URL(redirectURI).hostname.toLowerCase();
    if (host === 'chatgpt.com' || host.endsWith('.chatgpt.com')) return { id: 'chatgpt', name: 'ChatGPT' };
    if (host === 'claude.ai' || host.endsWith('.claude.ai')) return { id: 'claude', name: 'Claude' };
  } catch {
    // The backend performs the authoritative redirect URI validation.
  }
  return { id: 'mcp', name: 'AI application' };
};

function PermissionRow({ icon, title, description }) {
  return <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}><Box sx={{ color: 'text.secondary', display: 'flex', mt: 0.15 }}>{icon}</Box><Box sx={{ flex: 1 }}><Typography variant="subtitle2" fontWeight={700}>{title}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{description}</Typography></Box><ExpandMoreRoundedIcon color="disabled" fontSize="small" /></Stack>;
}

export default function MCPAuthorize() {
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const tr = useCallback((key, fallback, values = {}) => t(`mcp.${key}`, { defaultValue: fallback, ...values }), [t]);
  const [data, setData] = useState(null);
  const [organization, setOrganization] = useState('');
  const [allowWrite, setAllowWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const signedIn = Boolean(LocalStorage.getItem('token'));
  const hasOrgAdminPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.ORG_ADMIN);
  const params = new URLSearchParams(window.location.search);
  const wantsWrite = (params.get('scope') || '').split(/\s+/).includes('brand2social:write');
  const validRequest = requiredOAuthKeys.every((key) => params.get(key));
  const provider = providerFromRedirect(params.get('redirect_uri') || '');
  const providerName = provider.id === 'mcp' ? tr('ai_application', 'AI application') : provider.name;

  const reportError = useCallback((message) => {
    const value = message || tr('request_failed', 'Unable to complete the request. Please try again.');
    setError(value);
    showToastRef.current(value, 'e');
  }, [tr]);

  useEffect(() => {
    if (!signedIn) { setLoading(false); return undefined; }
    if (!hasOrgAdminPermission) {
      reportError(tr('org_admin_required', 'Only an organization administrator can connect or manage AI applications.'));
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    HttpServices.Get({
      url: mcpModuleURL.connections,
      signal: controller.signal,
      successCallback: (result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setOrganization(result.organizations[0]?.id || '');
        setLoading(false);
      },
      failureCallback: (message) => { if (!controller.signal.aborted) { reportError(message); setLoading(false); } },
    });
    return () => controller.abort();
  }, [hasOrgAdminPermission, reportError, signedIn, tr]);

  const authorize = () => {
    if (busy || !hasOrgAdminPermission || !organization || !validRequest || (wantsWrite && !allowWrite)) return;
    setBusy(true);
    setError('');
    const body = { org_id: Number(organization), allow_write: wantsWrite && allowWrite };
    oauthKeys.forEach((key) => { body[key] = params.get(key) || ''; });
    HttpServices.Post({
      url: mcpModuleURL.authorize,
      body,
      successCallback: (result) => {
        try {
          const destination = new URL(result.redirect_url);
          const expected = new URL(body.redirect_uri);
          if (destination.protocol !== 'https:' || destination.origin !== expected.origin || destination.pathname !== expected.pathname) throw new Error('Invalid redirect');
          window.location.assign(destination.href);
        } catch {
          reportError(tr('redirect_error', 'Unable to return to your AI application. Start the connection again.'));
          setBusy(false);
        }
      },
      failureCallback: (message) => { reportError(message); setBusy(false); },
    });
  };

  const permissions = [
    { icon: <PersonOutlineRoundedIcon fontSize="small" />, title: tr('permission_account', 'Your account'), description: tr('permission_account_detail', 'Identify the Brand2Social user authorizing this connection.') },
    { icon: <BusinessOutlinedIcon fontSize="small" />, title: tr('permission_org', 'Your organization and brands'), description: tr('permission_org_detail', 'View the selected organization, permitted brands and connected profiles.') },
    { icon: <ArticleOutlinedIcon fontSize="small" />, title: tr('permission_posts', 'Your content'), description: tr('permission_posts_detail', 'Review drafts, scheduled, approval, elapsed, failed and published posts.') },
    ...(wantsWrite ? [{ icon: <CloudUploadOutlinedIcon fontSize="small" />, title: tr('permission_media', 'Media and drafts'), description: tr('permission_media_detail', 'Upload media and create drafts using your existing permissions.') }, { icon: <EditCalendarOutlinedIcon fontSize="small" />, title: tr('permission_publish', 'Scheduling and publishing'), description: tr('permission_publish_detail', 'Schedule or submit posts. Existing approval rules remain active.') }] : []),
  ];

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f7f7f5', py: { xs: 2, md: 7 }, px: 2 }}>
      <Card variant="outlined" sx={{ maxWidth: 760, mx: 'auto', borderRadius: 3, overflow: 'hidden', boxShadow: '0 18px 50px rgba(29, 41, 35, .08)' }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <ProviderMark provider={provider.id} size={58} />
            <Box sx={{ width: 42, height: 1, bgcolor: 'divider', position: 'relative', '&::after': { content: '""', position: 'absolute', width: 8, height: 8, border: '2px solid', borderColor: 'text.secondary', borderRadius: 0.5, top: -4, left: 17, bgcolor: 'background.paper' } }} />
            <ProviderMark provider="mcp" size={58} />
          </Stack>
          <Typography variant="h4" fontWeight={700} textAlign="center">{tr('provider_wants_connect', '{{provider}} wants to connect to Brand2Social', { provider: providerName })}</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>{tr('consent_subtitle', 'Review the organization and permissions before connecting.')}</Typography>

          {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
          {!validRequest && <Alert severity="warning" sx={{ mt: 3 }}>{tr('start_claude', 'Start this connection from ChatGPT or Claude using the Brand2Social MCP endpoint.')}</Alert>}

          {!signedIn ? <Stack spacing={2} sx={{ mt: 3 }}><Alert severity="info">{tr('login_help', 'Sign in to Brand2Social in a new tab, then return here and reload.')}</Alert><Button href="/login" target="_blank" rel="noopener noreferrer" variant="contained">{tr('login', 'Sign in')}</Button><Button onClick={() => window.location.reload()}>{tr('reload', 'Reload')}</Button></Stack>
            : !hasOrgAdminPermission ? null
              : loading ? <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label={tr('loading_organizations', 'Loading organizations')} /></Box> : <>
                <TextField fullWidth select label={tr('organization', 'Organization')} value={organization} onChange={(event) => setOrganization(event.target.value)} disabled={busy} sx={{ mt: 3 }}>{(data?.organizations || []).map((org) => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}</TextField>

                <Box sx={{ mt: 2.5, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}><Typography fontWeight={700}>{tr('will_gain_access', '{{provider}} will gain access to:', { provider: providerName })}</Typography><Chip size="small" color={wantsWrite ? 'warning' : 'primary'} variant="outlined" label={wantsWrite ? tr('write_access', 'Read and write') : tr('read_only', 'Read only')} /></Stack>
                  <Stack divider={<Divider flexItem />}>{permissions.map((permission) => <PermissionRow key={permission.title} {...permission} />)}</Stack>
                </Box>

                {wantsWrite && <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#fff8e8', border: '1px solid #f1d493' }}><FormControlLabel sx={{ m: 0, alignItems: 'flex-start' }} control={<Checkbox checked={allowWrite} onChange={(event) => setAllowWrite(event.target.checked)} disabled={busy} sx={{ pt: 0 }} />} label={<Box><Typography variant="subtitle2" fontWeight={700}>{tr('confirm_write_title', 'Allow content creation and publishing')}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>{tr('allow_write', 'Allow this AI application to upload media, create drafts, and schedule or publish posts using my existing permissions.')}</Typography></Box>} /></Box>}

                <Alert severity="info" icon={false} sx={{ mt: 2.5 }}>{tr('trust_note', 'Only connect {{provider}} if you trust it. You can delete this connection at any time from AI Connections.', { provider: providerName })}</Alert>
                <Divider sx={{ mx: { xs: -2.5, md: -4 }, mt: 3 }} />
                <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ pt: 2.5 }}><Button href="/ai-connections" disabled={busy}>{tr('cancel', 'Cancel')}</Button><Button variant="contained" disabled={busy || !organization || !validRequest || !data || (wantsWrite && !allowWrite)} onClick={authorize}>{busy ? tr('connecting', 'Connecting…') : tr('connect_provider', 'Connect {{provider}}', { provider: providerName })}</Button></Stack>
              </>}
        </CardContent>
      </Card>
    </Box>
  );
}
